import { Box, Paper, Stack, Typography } from "@mui/material";

interface MobileBarChartDatum {
  label: string;
  value: number;
}

interface MobileBarChartProps {
  title: string;
  data: MobileBarChartDatum[];
  color: string;
  barLabelFormatter?: (value: number) => string;
}

const BAR_AREA_HEIGHT = 160;
const COLUMN_WIDTH = 64;

// Mobile's replacement for this module's desktop bar-chart pattern
// (PerformanceCharts.tsx / BrandsIncomeChart.tsx / UsersStepBarSection.tsx's
// CategoryBarChart) - those rely on BarItemTooltip.tsx, a tooltip that
// follows the cursor on hover, invisible on a touch device with no
// persistent hover state.
//
// This is deliberately plain CSS, not @mui/x-charts: an earlier attempt
// used the library's own `barLabel` prop for a static value label, but that
// draws the label INSIDE the bar's own box, computed by the library - once
// bars got this thin (e.g. 30 daily bars in ~350px) there was no room for
// the text and every label overlapped its neighbors (see the module's
// handoff summary for the before/after). Here each bar's value is its own
// separate DOM element sitting ABOVE the bar, so it can never collide with
// the bar or with a neighboring label - and every category gets a fixed
// column width with the row scrolling horizontally (this app's established
// "swipe to see more" convention for anything too wide for the screen, also
// used by the mockup's own charts for the same reason) so bars/labels never
// get squeezed no matter how many days/categories there are.
export default function MobileBarChart({ title, data, color, barLabelFormatter = String }: MobileBarChartProps) {
  const max = Math.max(...data.map((entry) => entry.value), 1);

  return (
    <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontSize: 13.5 }}>
        {title}
      </Typography>
      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <Stack direction="row" spacing={0.5} sx={{ minWidth: data.length * COLUMN_WIDTH }}>
          {data.map((entry, index) => {
            const heightPercent = max ? (entry.value / max) * 100 : 0;
            return (
              <Stack
                key={`${entry.label}-${index}`}
                alignItems="center"
                spacing={0.5}
                sx={{ width: COLUMN_WIDTH, flexShrink: 0 }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#424242", whiteSpace: "nowrap" }}>
                  {barLabelFormatter(entry.value)}
                </Typography>
                <Box sx={{ width: "100%", height: BAR_AREA_HEIGHT, display: "flex", alignItems: "flex-end" }}>
                  <Box
                    sx={{
                      width: "70%",
                      mx: "auto",
                      height: `${entry.value > 0 ? Math.max(heightPercent, 2) : 0}%`,
                      backgroundColor: color,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 9.5,
                    color: "#9e9e9e",
                    textAlign: "center",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {entry.label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
}
